import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, PlayCircle, Search } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

const embedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

const VideoCard = ({ v }) => {
  const [playing, setPlaying] = useState(false);
  const embed = embedUrl(v.video_url);
  return (
    <div className="rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid={`video-card-${v.video_id}`}>
      <div className="aspect-video bg-bg-elevated relative">
        {playing && embed ? (
          <iframe src={`${embed}?autoplay=1`} title={v.title} allow="autoplay; fullscreen"
            allowFullScreen className="absolute inset-0 h-full w-full" />
        ) : v.video_url ? (
          <button type="button" data-testid={`video-play-${v.video_id}`}
            onClick={() => (embed ? setPlaying(true) : window.open(v.video_url, "_blank"))}
            className="absolute inset-0 flex items-center justify-center hover:bg-black/25 transition">
            <PlayCircle className="h-10 w-10 text-brand-amber" />
          </button>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <PlayCircle className="h-9 w-9 text-ink-muted" />
            <div className="font-mono text-[8px] uppercase tracking-wider text-ink-muted mt-1.5">Video coming soon</div>
          </div>
        )}
      </div>
      <div className="p-3.5">
        <div className="text-sm text-ink-primary font-semibold leading-snug">{v.title}</div>
        <div className="text-[11px] text-ink-muted mt-1 line-clamp-2">{v.description}</div>
        <div className="flex items-center gap-2 mt-2 font-mono text-[9px] uppercase tracking-wider text-ink-muted">
          <span className="text-brand-amber">{v.category}</span>
          <span>{v.difficulty}</span>
          {v.duration && <span>{v.duration}</span>}
        </div>
      </div>
    </div>
  );
};

export default function HelpVideos() {
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [level, setLevel] = useState("");

  useEffect(() => { api.get("/help/videos").then((r) => setVideos(r.data.videos)).catch(() => {}); }, []);

  const cats = useMemo(() => [...new Set(videos.map((v) => v.category))], [videos]);
  const shown = videos.filter((v) =>
    (!q || `${v.title} ${v.description} ${v.category}`.toLowerCase().includes(q.toLowerCase())) &&
    (!cat || v.category === cat) && (!level || v.difficulty === level));

  return (
    <div className="space-y-6" data-testid="help-videos-page">
      <Link to="/dashboard/help" className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary">
        <ArrowLeft className="h-3.5 w-3.5" /> Help Center
      </Link>
      <PageHeader eyebrow="// HELP · VIDEO TUTORIALS" title="Video Tutorial Library"
        description="Step-by-step walkthroughs for every part of BroadcastFlow AI — from first station setup to advanced automations." />
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="videos-search-input"
            placeholder="Search tutorials…"
            className="w-full rounded-md border border-line bg-bg pl-9 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="videos-category-filter"
          className="rounded-md border border-line bg-bg px-2.5 py-1.5 text-xs text-ink-primary focus:outline-none">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="videos-level-filter"
          className="rounded-md border border-line bg-bg px-2.5 py-1.5 text-xs text-ink-primary focus:outline-none">
          <option value="">All levels</option>
          {["Beginner", "Intermediate", "Advanced"].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="videos-grid">
        {shown.map((v) => <VideoCard key={v.video_id} v={v} />)}
      </div>
      {shown.length === 0 && <div className="text-sm text-ink-muted text-center py-8">No tutorials match your filters.</div>}
    </div>
  );
}
